<template>
    <img
        v-if="previewUrl"
        :src="previewUrl"
        :alt="id"
        class="preview"
        :class="{ 'preview--loading': isLoading }"
        @load="onImageLoad"
        @error="onImageError"
    />
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import type { S3FileLocation } from "shared-types/indesign";
import type { InDesignMasterTemplate } from "shared-types";

import type { InDesignComputedOverwrite } from "@/types/types";
import { BannerEvents } from "@/enums/banners";
import { requestIndesignPreview } from "@/utils/CreativeIntelligenceApi/indesign";
import { getRenditionPreviewRequestBody } from "@/services/InDesignPreview";

function isAbortError(error: unknown) {
    return error instanceof Error && error.name === "AbortError";
}

const emit = defineEmits(["loading", BannerEvents.BannerLoaded, BannerEvents.BannerError]);

const { id, previewValues } = defineProps<{
    id: string;
    previewValues: string;
}>();

const route = useRoute();
const previewUrl = ref<string | null>(null);
const isLoading = ref(false);
let activePreviewController: AbortController | null = null;

const data = computed(() => {
    return JSON.parse(previewValues) as {
        clientId?: string;
        computedOverwrites?: InDesignComputedOverwrite[];
        targetSize?: {
            width?: number;
            height?: number;
            unit?: string;
        };
        masterTemplate: Omit<InDesignMasterTemplate, "sourceDocuments"> & {
            sourceDocuments: S3FileLocation[];
        };
    };
});

const clientId = computed(() => data.value.clientId || (route.params.clientId as string));

const sourceDocuments = computed(() => {
    return Array.isArray(data.value.masterTemplate.sourceDocuments) ? data.value.masterTemplate.sourceDocuments : [];
});

function cancelActivePreviewRequest() {
    activePreviewController?.abort();
    activePreviewController = null;
}

const renditionRequestBody = computed(() =>
    getRenditionPreviewRequestBody({
        clientId: clientId.value,
        computedOverwrites: data.value.computedOverwrites,
        masterTemplate: data.value.masterTemplate,
        sourceDocuments: sourceDocuments.value,
        targetSize: data.value.targetSize
    })
);

async function startPreviewRequest() {
    cancelActivePreviewRequest();

    const controller = new AbortController();
    activePreviewController = controller;

    try {
        isLoading.value = true;
        emit("loading", true);

        previewUrl.value = await requestIndesignPreview({
            signal: controller.signal,
            clientId: clientId.value,
            sourceDocuments: sourceDocuments.value,
            renditionRequestBody: renditionRequestBody.value
        });
    } catch (error) {
        if (isAbortError(error)) {
            return;
        }

        emit(BannerEvents.BannerError, error instanceof Error ? error.message : "Failed to request InDesign preview");
    } finally {
        if (activePreviewController === controller) {
            activePreviewController = null;
        }
    }
}

function onImageLoad() {
    isLoading.value = false;
    emit("loading", false);
    emit(BannerEvents.BannerLoaded);
}

function onImageError() {
    isLoading.value = false;
    emit(BannerEvents.BannerError, "There was an error loading the InDesign preview");
}

watch(
    renditionRequestBody,
    () => {
        void startPreviewRequest();
    },
    { deep: true, immediate: true }
);

onBeforeUnmount(() => {
    cancelActivePreviewRequest();
});
</script>

<style scoped>
.preview {
    width: 100%;
}

.preview--loading {
    visibility: hidden;
}
</style>
